import { t } from '@if/core/i18n'
import DeleteIcon from '@mui/icons-material/Delete'
import EditIcon from '@mui/icons-material/Edit'
import HelpIcon from '@mui/icons-material/Help'
import { IconButton, Stack, Tooltip, Typography } from '@mui/material'
import { DataGrid, useGridApiRef } from '@mui/x-data-grid'
import { Metro2ReportingSchemaType } from '@src/components/LoanForm/loan-form-schema'
import { EMetro2ReportingReason } from '@src/types/constants'
import { useUser } from '@src/providers/UserProvider'
import { Metro2Reporting } from '@src/types/loan/loan-calendar'
import { User } from '@src/types/user'
import { DateOnly } from 'node_modules/@if/core/dist/date/dateOnly'
import { JSX, useEffect, useMemo, useState } from 'react'
import FormDialog from '../../Common/Dialog/FormDialog'
import EditMetro2Reporting from './EditMetro2Reporting'
import { useMetro2DialogColumns } from './Metro2ReportingDialog-hook'

interface Props {
  onCancel: () => void
  onSave: (data: Metro2Reporting | null) => void
  open: boolean
  currentMetro2Reporting: Metro2Reporting | null
  historyMetro2Reportings: Metro2Reporting[]
}

// Create a new Metro2ReportingSchemaType.
// If one exists, take it as a base. Else return null as the form will automatically fill with defaults.
// Always place username to the current user, and createDT to the current time.
const createNewMetro2Reporting = (
  currentMetro2Reporting: Metro2Reporting | null,
  user: User | undefined,
): Metro2ReportingSchemaType | null => {
  const metro2ReportingSchema: Metro2ReportingSchemaType | null = {
    id: null,
    reported: false,
    metro2OverrideDate: new DateOnly(),
    // Unselected at creation; the required-enum schema (default null) forces a choice before save.
    metro2ReportingReason: null as unknown as EMetro2ReportingReason,
    description: '',
    createdOn: new Date(),
    createdByUserFullname: user?.name ?? '-',
    createdByUserId: user?.id ?? '0',
  }
  if (currentMetro2Reporting) {
    metro2ReportingSchema.metro2OverrideDate = currentMetro2Reporting.metro2OverrideDate
    metro2ReportingSchema.metro2ReportingReason = currentMetro2Reporting.metro2ReportingReason
    metro2ReportingSchema.description = currentMetro2Reporting.description
  }
  return metro2ReportingSchema
}

const canBeDeleted = (currentMetro2ReportingDate: Metro2Reporting | null): boolean => {
  if (!currentMetro2ReportingDate) {
    return false
  }
  // Can be deleted only if the date is in the current month or future months
  const today = new Date()
  const firstDayOfCurrentMonth = new Date(today.getFullYear(), today.getMonth(), 1)
  return currentMetro2ReportingDate.metro2OverrideDate.getTime() >= firstDayOfCurrentMonth.getTime()
}

const selectPreviousReportedMetro2Reporting = (
  currentMetro2Reporting: Metro2Reporting | null,
  historyMetro2Reportings: Metro2Reporting[],
): Metro2Reporting | null => {
  if (!currentMetro2Reporting || !historyMetro2Reportings || historyMetro2Reportings.length === 0) {
    return null
  }

  const previousReporting = historyMetro2Reportings
    .filter((report) => report.reported)
    .sort((a, b) => b.metro2OverrideDate.getTime() - a.metro2OverrideDate.getTime())[0]
  return previousReporting || null
}

const Metro2ReportingDialog = ({
  onCancel,
  onSave,
  open,
  currentMetro2Reporting,
  historyMetro2Reportings,
}: Props): JSX.Element => {
  const apiRef = useGridApiRef()
  const columns = useMetro2DialogColumns()
  const user = useUser()

  const [currentMetro2ReportingForm, setCurrentMetro2ReportingForm] = useState<Metro2Reporting | null>(
    currentMetro2Reporting,
  )

  const [canBeSaved, setCanBeSaved] = useState(false)

  const [editDialogOpen, setEditDialogOpen] = useState(false)

  useEffect(() => {
    if (open) {
      setCurrentMetro2ReportingForm(currentMetro2Reporting)
    }
  }, [open, currentMetro2Reporting])

  const createCurrentMetro2ReportingToolTip = useMemo(() => {
    return (
      <Stack spacing={1}>
        <Typography variant="body2">
          <strong>{t('loan.payment.creditReportDialog.date')}:</strong>{' '}
          {currentMetro2ReportingForm?.metro2OverrideDate?.toString() || t('common.none')}
        </Typography>
        <Typography variant="body2">
          <strong>{t('loan.payment.creditReportDialog.reason')}:</strong>{' '}
          {currentMetro2ReportingForm
            ? t(`enum.EMetro2ReportingReason.${currentMetro2ReportingForm.metro2ReportingReason}`)
            : t('common.none')}
        </Typography>
        <Typography variant="body2">
          <strong>{t('loan.payment.creditReportDialog.createdBy')}:</strong>{' '}
          {currentMetro2ReportingForm?.createdByUserFullname || t('loan.payment.creditReportDialog.system')}
        </Typography>
        <Typography variant="body2">
          <strong>{t('loan.payment.creditReportDialog.createdOn')}:</strong>{' '}
          {currentMetro2ReportingForm?.createdOn.toLocaleString() || t('common.none')}
        </Typography>
        <Typography variant="body2">
          <strong>{t('loan.payment.creditReportDialog.desc')}:</strong>{' '}
          {currentMetro2ReportingForm?.description || t('common.none')}
        </Typography>
      </Stack>
    )
  }, [currentMetro2ReportingForm])

  const onSubmitHandler = (data: Metro2Reporting | null) => {
    onSave(data)
    onCancelHandler()
  }

  const onCancelHandler = () => {
    setCanBeSaved(false)
    onCancel()
  }

  return (
    <FormDialog
      onClose={onCancelHandler}
      open={open}
      maxWidth="lg"
      confirmLabel={t('common.save')}
      title={<Typography>{t('loan.payment.creditReportDialog.title')}</Typography>}
      onConfirm={() => onSubmitHandler(currentMetro2ReportingForm)}
      disabled={!canBeSaved}
    >
      <Typography variant="subtitle2">{t('loan.payment.creditReportDialog.descriptionDialog')}</Typography>
      <Stack direction="row" spacing={1} alignItems="center">
        <Typography variant="body2" color="textSecondary" sx={{ marginTop: 2 }}>
          {t('loan.payment.creditReportDialog.currentDate', { date: '' })}
          <strong>
            {currentMetro2ReportingForm ? currentMetro2ReportingForm?.metro2OverrideDate?.toString() : t('common.none')}
          </strong>
        </Typography>
        <Tooltip title={createCurrentMetro2ReportingToolTip}>
          <HelpIcon color="primary" fontSize="inherit" />
        </Tooltip>
        <Stack direction="row" spacing={0} alignItems="center">
          <Tooltip title={t('loan.payment.creditReportDialog.editDate')}>
            <IconButton
              size="small"
              onClick={() => {
                setEditDialogOpen(true)
              }}
            >
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title={t('loan.payment.creditReportDialog.deleteDate')}>
            <span>
              <IconButton
                size="small"
                onClick={() => {
                  setCurrentMetro2ReportingForm(
                    selectPreviousReportedMetro2Reporting(currentMetro2ReportingForm, historyMetro2Reportings),
                  )
                  setCanBeSaved(true)
                }}
                disabled={!canBeDeleted(currentMetro2ReportingForm)}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        </Stack>
      </Stack>
      <Typography variant="subtitle2" sx={{ marginTop: 2 }}>
        {t('loan.payment.creditReportDialog.history')}
      </Typography>
      <DataGrid
        apiRef={apiRef}
        disableColumnFilter
        rows={historyMetro2Reportings}
        columns={columns}
        getRowId={(row) => row.id!}
        initialState={{
          sorting: {
            sortModel: [{ field: 'createdOn', sort: 'desc' }],
          },
        }}
      />
      <EditMetro2Reporting
        open={editDialogOpen}
        onClose={() => setEditDialogOpen(false)}
        value={createNewMetro2Reporting(currentMetro2ReportingForm, user)}
        onSubmit={(updatedData: Metro2ReportingSchemaType) => {
          setEditDialogOpen(false)
          setCurrentMetro2ReportingForm({ ...updatedData } as Metro2Reporting)
          setCanBeSaved(true)
        }}
      />
    </FormDialog>
  )
}

export default Metro2ReportingDialog
